// Types shared by the API, the web app and the native app. The package has no runtime dependencies: the hooks
// (./hooks) take the caller's React Query, and React Query is only a dev dependency here, for its types.
export const API_PREFIX = "/api";
export const BIO_MAX = 140;
export const STAMPS_PER_CARD = 12;
export const JOIN_NOTE_MAX = 280;
/** Club sizes: small under 10 members, medium 10–50, large over 50. */
export const SIZE_BANDS = { small: [1, 9], medium: [10, 50], large: [51, Infinity] };
/** The genres an admin can pick from, so pills match across clubs. */
export const CLUB_GENRES = [
    "Literary fiction", "Contemporary fiction", "Science fiction", "Fantasy", "Mystery", "Thriller", "Horror", "Romance",
    "Historical fiction", "Classics", "Short stories", "Poetry", "Graphic novels", "Young adult",
    "Nonfiction", "Memoir", "Biography", "History", "Science", "Philosophy", "Essays", "True crime", "Self-help", "Humor",
];
export const MAX_CLUB_GENRES = 5;
/**
 * Usual pace in three bands, pages a day. One table for the creation flow's
 * presets and anything that groups clubs by pace: a band covers [min, max),
 * and `preset` is the number its button sets.
 */
export const PACE_BANDS = [
    { key: "relaxed", label: "Relaxed", min: 1, max: 15, preset: 10 },
    { key: "steady", label: "Steady", min: 15, max: 30, preset: 20 },
    { key: "fast", label: "Fast", min: 30, max: Infinity, preset: 40 },
];
/** Which band a pace falls in; null for no pace. */
export const paceBand = (pace) => pace == null || !Number.isFinite(pace) || pace < 1 ? null : PACE_BANDS.find((b) => pace >= b.min && pace < b.max)?.key ?? null;
// ---- the noticeboard (club talk that isn't about the book; no spoiler gating)
export const BOARD_MAX_BODY = 1000;
// ---- unsealing moments, notes left forward, predictions (PLAN.md §Unsealing moments)
/** Percent of the book under which a reader may still write (and edit) a prediction. */
export const PREDICTION_WINDOW = 15;
export const PREDICTION_MAX_BODY = 280;
export const FORWARD_NOTE_MAX_BODY = 500;
/** Live notes one writer may leave forward in one book. */
export const FORWARD_NOTE_LIMIT = 10;
// ---- email addresses and email sign-in (PLAN.md §Email addresses and email sign-in)
export const EMAIL_CODE_LENGTH = 6;
// ---- notifications (PLAN.md §Notifications)
/** Groups in Settings → Notifications, in order. */
export const NOTIFICATION_SECTIONS = [
    { key: "you", label: "For you" },
    { key: "meetings", label: "Meetings" },
    { key: "picking", label: "Picking books" },
    { key: "running", label: "Running a club" },
    { key: "activity", label: "Club activity" },
];
/** Every notification type with its default. The server adds how each one is sent and worded. */
export const NOTIFICATION_TYPES = [
    { key: "reply", section: "you", label: "Replies and mentions", about: "Someone replies to you or mentions you, in Talk or on the noticeboard.", default: true },
    { key: "answer", section: "you", label: "Answers to your questions", about: "Someone answers a question you asked people ahead.", default: true },
    { key: "note_forward", section: "you", label: "Notes left for you", about: "A note someone left at a page reaches you.", default: true, available: false },
    { key: "join_accepted", section: "you", label: "You were let in", about: "A club you asked to join said yes.", default: true },
    { key: "join_declined", section: "you", label: "Your request wasn't accepted", about: "A club you asked to join said no.", default: true, inboxOnly: true },
    { key: "post_moved", section: "you", label: "Your post was moved", about: "Someone moved your noticeboard post into the book's discussion.", default: true, inboxOnly: true },
    { key: "meeting_set", section: "meetings", label: "Meeting scheduled or moved", about: "A date is set, or it changes.", default: true },
    { key: "meeting_tomorrow", section: "meetings", label: "Meeting tomorrow", about: "If you're going, or might.", default: true },
    { key: "rsvp_nudge", section: "meetings", label: "Are you coming?", about: "Two days before, if you haven't said. Sent once.", default: true },
    { key: "book_started", section: "picking", label: "New book started", about: "Your club starts its next book.", default: true },
    { key: "vote", section: "picking", label: "Votes", about: "A vote opens, and a day before it closes if you haven't voted.", default: true },
    { key: "your_turn", section: "picking", label: "Your turn to pick", about: "In clubs that take turns.", default: true },
    { key: "join_request", section: "running", label: "Join requests", about: "Someone asks to join. Several at once arrive together.", default: true },
    { key: "role", section: "running", label: "New role", about: "Someone makes you an admin or owner.", default: true },
    { key: "claim", section: "running", label: "Someone claiming your club", about: "Always sent, because it affects your account.", default: true, critical: true, available: false },
    { key: "talk_new", section: "activity", label: "New Talk messages", about: "How many new messages you can read. Never what they say.", default: false },
    { key: "club_activity", section: "activity", label: "Daily summary", about: "New suggestions, members and finished books, once a day at 6 PM.", default: false },
    { key: "security", section: "you", label: "Sign-in and security", about: "A new way to sign in was added to your account. Always emailed.", default: true, critical: true, emailOnly: true },
];
export * from "./spoilers.js";
/** A brand-new account with nothing in it: probably someone who already has one under another sign-in. */
export function looksLikeWrongDoor(me, now = Date.now()) {
    return me.clubs.length === 0 && me.accounts.length <= 1 && now - new Date(me.createdAt).getTime() < 60 * 60_000;
}
export * from "./stamps.js";
export * from "./widget.js";
export * from "./plan.js";
//# sourceMappingURL=index.js.map